import React, { useState, useEffect, useRef } from "react";
import DefaultLayout from "../../components/DefaultLayout";
import { showNotification } from "../../utils/error";
import axiosInstance from "../../utils/axiosInstance";
import loader from "../../assets/button-loader.svg";

// Simple markdown-to-HTML parser function for preview
const parseMarkdown = (text) => {
  if (!text) return "";
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Bold (**text**)
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  
  // Italic (*text*)
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  
  // Headers (# header)
  html = html.replace(/^#\s+(.*?)$/gm, '<h1 class="text-2xl font-bold text-white my-3">$1</h1>');
  html = html.replace(/^##\s+(.*?)$/gm, '<h2 class="text-xl font-bold text-white my-2">$1</h2>');
  html = html.replace(/^###\s+(.*?)$/gm, '<h3 class="text-lg font-bold text-white my-2">$1</h3>');
  
  // Links ([label](url))
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-indigo-400 hover:underline">$1</a>');
  
  // Bullet Points (- item)
  html = html.replace(/^\s*-\s+(.*?)$/gm, '<li class="ml-4 list-disc text-gray-300">$1</li>');

  // Line breaks
  html = html.replace(/\n/g, "<br />");
  
  return html;
};

// Default mock letters
const defaultHistory = [
  {
    id: 1,
    subject: "Welcome to Somos Blockchain!",
    category: "Notice",
    recipients: "All Users",
    date: "2026-06-25 10:14:02",
    content: "# Welcome to Somos Blockchain!\n\nWe are excited to have you on board. Stay tuned for monthly updates, wallet notifications, and new subscription benefits.\n\nBest regards,\n**Somos Team**"
  },
  {
    id: 2,
    subject: "Monthly Newsletter - June 2026",
    category: "News",
    recipients: "Subscribed Only",
    date: "2026-06-30 08:30:15",
    content: "Dear Subscribers,\n\nHere are the top blockchain trends for June 2026:\n- Ethereum gas fees optimization\n- SOMOS token utility expansion\n- New staking pools available next week!\n\nMake sure your wallet has active subscription to get these updates directly.\n\nRead more at [Somos Website](https://somosblockchain.com/)."
  },
  {
    id: 3,
    subject: "Security Update: Wallet Verification",
    category: "Alert",
    recipients: "All Users",
    date: "2026-06-15 14:22:50",
    content: "## Security Notice\n\nPlease ensure you never share your private keys or seed phrase. We will **never** ask for your credentials. Always verify the domain name is `somosblockchain.com` before connecting your wallet."
  }
];

const NewLetter = () => {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("Notice");
  const [recipients, setRecipients] = useState("All Users");
  const [content, setContent] = useState("");
  const [activeTab, setActiveTab] = useState("write"); // 'write' or 'preview'
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState(null); // For view modal
  
  const textareaRef = useRef(null);

  // Load history from localStorage or defaults
  useEffect(() => {
    const saved = localStorage.getItem("somos_sent_letters");
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        setHistory(defaultHistory);
      }
    } else {
      setHistory(defaultHistory);
      localStorage.setItem("somos_sent_letters", JSON.stringify(defaultHistory));
    }
  }, []);

  // Format date helper
  const getCurrentDateTime = () => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    const date = new Date();
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      " " +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes()) +
      ":" +
      pad(date.getSeconds())
    );
  };

  // Helper to insert markdown tags at cursor selection
  const insertTag = (tagStart, tagEnd = "") => {
    if (!textareaRef.current) return;
    const txtarea = textareaRef.current;
    const start = txtarea.selectionStart;
    const end = txtarea.selectionEnd;
    const text = txtarea.value;
    const selectedText = text.substring(start, end);
    const replacement = tagStart + (selectedText || "text") + tagEnd;
    
    setContent(text.substring(0, start) + replacement + text.substring(end));
    
    // Reset focus and selection
    setTimeout(() => {
      txtarea.focus();
      txtarea.setSelectionRange(
        start + tagStart.length,
        start + tagStart.length + (selectedText || "text").length
      );
    }, 0);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      showNotification("error", "Subject is required.");
      return;
    }
    if (!content.trim()) {
      showNotification("error", "Newsletter content is required.");
      return;
    }

    setLoading(true);
    const payload = {
      subject: subject.trim(),
      category: category,
      recipients: recipients,
      content: content.trim(),
      date: getCurrentDateTime()
    };

    try {
      // Attempt backend API post
      const response = await axiosInstance.post("/letter/send", payload);
      if (response?.data?.success) {
        showNotification("success", "Newsletter sent successfully.");
      } else {
        // If api call responds but success is false
        throw new Error(response?.data?.message || "Failed to send");
      }
    } catch (err) {
      console.warn("Backend API not configured or failed, falling back to local simulation.", err);
      // We will perform local simulation for developer demonstration so the user gets fully working functionality
    }

    // Add to history and update state/localStorage
    const newHistory = [
      {
        id: Date.now(),
        ...payload
      },
      ...history
    ];
    setHistory(newHistory);
    localStorage.setItem("somos_sent_letters", JSON.stringify(newHistory));
    
    // Clear form
    setSubject("");
    setCategory("Notice");
    setRecipients("All Users");
    setContent("");
    setActiveTab("write");
    
    showNotification("success", "Newsletter sent and recorded successfully.");
    setLoading(false);
  };

  const handleClear = () => {
    setSubject("");
    setCategory("Notice");
    setRecipients("All Users");
    setContent("");
    setActiveTab("write");
  };

  const handleDeleteHistory = (id) => {
    const newHistory = history.filter((item) => item.id !== id);
    setHistory(newHistory);
    localStorage.setItem("somos_sent_letters", JSON.stringify(newHistory));
    showNotification("success", "Sent record removed from log.");
    if (selectedLetter && selectedLetter.id === id) {
      setSelectedLetter(null);
    }
  };

  // Filter history based on search term
  const filteredHistory = history.filter(
    (item) =>
      item.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.recipients.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DefaultLayout>
      <div className="w-full h-full px-6 py-2">
        <div className="p-2 mb-6">
          <h1 className="text-2xl font-semibold text-white">Newsletter</h1>
          <p className="text-sm text-white/80 mt-1">Compose and send email newsletters, updates, or alerts to your system users</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
          {/* COMPOSE SECTION */}
          <div className="xl:col-span-7 rounded-xl bg-[#1E1E1E] shadow-lg border border-[#313D4F] p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-5 h-5 mr-2 text-indigo-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
              Compose Newsletter
            </h2>

            <form onSubmit={handleSend} className="space-y-5">
              {/* Subject */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-[#EBEEF2] mb-2">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. System Maintenance Notice"
                  className="w-full text-base text-white bg-[#131212] rounded-xl border border-[#4B4B4B] focus:border-[#6552FE] px-4 py-3 outline-none transition-colors"
                />
              </div>

              {/* Category & Recipients row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-[#EBEEF2] mb-2">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full text-base text-white bg-[#131212] rounded-xl border border-[#4B4B4B] focus:border-[#6552FE] px-4 py-3 outline-none transition-colors appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'><path stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/></svg>")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 16px center",
                      backgroundSize: "16px"
                    }}
                  >
                    <option value="Notice">Notice</option>
                    <option value="News">News</option>
                    <option value="Alert">Alert</option>
                    <option value="Promotion">Promotion</option>
                    <option value="Update">Update</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-[#EBEEF2] mb-2">Recipients</label>
                  <select
                    value={recipients}
                    onChange={(e) => setRecipients(e.target.value)}
                    className="w-full text-base text-white bg-[#131212] rounded-xl border border-[#4B4B4B] focus:border-[#6552FE] px-4 py-3 outline-none transition-colors appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'><path stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/></svg>")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 16px center",
                      backgroundSize: "16px"
                    }}
                  >
                    <option value="All Users">All Users</option>
                    <option value="Subscribed Only">Subscribed Wallets Only</option>
                  </select>
                </div>
              </div>

              {/* Editor Tabs & Toolbar */}
              <div className="flex flex-col mt-2">
                <div className="flex justify-between items-center border-b border-[#313D4F] pb-2">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab("write")}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === "write"
                          ? "bg-[#6552FE] text-white"
                          : "text-[#A0AEC0] hover:text-white hover:bg-[#2D2D2D]"
                      }`}
                    >
                      Write
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("preview")}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === "preview"
                          ? "bg-[#6552FE] text-white"
                          : "text-[#A0AEC0] hover:text-white hover:bg-[#2D2D2D]"
                      }`}
                    >
                      Preview
                    </button>
                  </div>

                  {activeTab === "write" && (
                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={() => insertTag("**", "**")}
                        title="Bold Text"
                        className="p-1.5 rounded hover:bg-[#353535] text-[#D9D9D9] hover:text-white transition-colors"
                      >
                        <span className="font-bold text-xs">B</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => insertTag("*", "*")}
                        title="Italic Text"
                        className="p-1.5 rounded hover:bg-[#353535] text-[#D9D9D9] hover:text-white transition-colors"
                      >
                        <span className="italic text-xs">I</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => insertTag("# ")}
                        title="Heading"
                        className="p-1.5 rounded hover:bg-[#353535] text-[#D9D9D9] hover:text-white transition-colors text-xs font-semibold"
                      >
                        H1
                      </button>
                      <button
                        type="button"
                        onClick={() => insertTag("[", "](url)")}
                        title="Link"
                        className="p-1.5 rounded hover:bg-[#353535] text-[#D9D9D9] hover:text-white transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-3.5 h-3.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => insertTag("- ")}
                        title="List"
                        className="p-1.5 rounded hover:bg-[#353535] text-[#D9D9D9] hover:text-white transition-colors"
                      >
                        <span className="font-semibold text-xs">•-</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Editor Content Area */}
                <div className="mt-3">
                  {activeTab === "write" ? (
                    <textarea
                      ref={textareaRef}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Compose your message here... (Supports basic Markdown tags like **bold**, *italics*, # headers, list items - list, and links)"
                      rows={12}
                      className="w-full text-base text-white bg-[#131212] rounded-xl border border-[#4B4B4B] focus:border-[#6552FE] px-4 py-3 outline-none transition-colors resize-none font-mono placeholder:font-sans"
                    />
                  ) : (
                    <div
                      className="w-full h-[314px] overflow-y-auto text-base text-[#D9D9D9] bg-[#131212] rounded-xl border border-[#4B4B4B] px-5 py-4 select-text"
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(content) || '<span class="text-white/40 italic">Nothing to preview. Start writing in the Write tab!</span>' }}
                    />
                  )}
                </div>
              </div>

              {/* Form Action Buttons */}
              <div className="flex justify-end items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-6 py-2.5 rounded-xl text-[#F3F3F3] border border-[#4B4B4B] hover:bg-[#2d2d2d] font-semibold transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-[#6552FE] to-[#513FE6] hover:from-[#513FE6] hover:to-[#3F2CD0] text-white font-semibold flex items-center justify-center transition-all shadow-md shadow-indigo-900/30 min-w-[140px]"
                >
                  {loading ? (
                    <img src={loader} alt="Sending..." className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Send Newsletter
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4 ml-2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* HISTORY SECTION */}
          <div className="xl:col-span-5 rounded-xl bg-[#1E1E1E] shadow-lg border border-[#313D4F] p-6 h-[650px] flex flex-col">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-5 h-5 mr-2 text-indigo-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                Sent Logs ({filteredHistory.length})
              </h2>

              {/* Simple inline search */}
              <div className="relative w-full sm:w-48">
                <input
                  type="text"
                  placeholder="Search log..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-xs text-white bg-[#131212] rounded-lg border border-[#4B4B4B] focus:border-[#6552FE] pl-8 pr-2 py-1.5 outline-none transition-colors"
                />
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.602 10.602z" />
                </svg>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 space-y-3 no-scrollbar">
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-xl border border-[#313D4F] bg-[#131212]/50 hover:bg-[#131212] hover:border-[#6552FE]/50 transition-all flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start mb-1">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase ${
                            item.category === "Alert"
                              ? "bg-red-950 text-red-400 border border-red-900"
                              : item.category === "Notice"
                              ? "bg-blue-950 text-blue-400 border border-blue-900"
                              : item.category === "News"
                              ? "bg-green-950 text-green-400 border border-green-900"
                              : "bg-purple-950 text-purple-400 border border-purple-900"
                          }`}
                        >
                          {item.category}
                        </span>
                        <span className="text-[11px] text-[#A0AEC0]">{item.date}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-white truncate max-w-[280px] my-1" title={item.subject}>
                        {item.subject}
                      </h3>
                      <p className="text-xs text-[#A0AEC0] mb-2">Recipients: <span className="text-gray-300 font-medium">{item.recipients}</span></p>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-[#313D4F] pt-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setSelectedLetter(item)}
                        className="px-3 py-1 rounded bg-[#353535] hover:bg-[#484848] text-xs font-medium text-white transition-colors"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteHistory(item.id)}
                        className="p-1 rounded text-red-400 hover:bg-red-950 hover:text-red-300 transition-colors"
                        title="Delete record"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-full flex items-center justify-center text-[#A0AEC0] italic text-sm">
                  No letters match search filter
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* VIEW NEWSLETTER MODAL */}
      {selectedLetter && (
        <div className="fixed inset-0 z-99999 overflow-auto bg-black bg-opacity-70 flex items-center justify-center p-4">
          <div className="bg-[#1E1E1E] border border-[#313D4F] rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#313D4F]">
              <div>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-indigo-950 text-indigo-400 border border-indigo-900 mr-2">
                  {selectedLetter.category}
                </span>
                <span className="text-xs text-[#A0AEC0]">{selectedLetter.date}</span>
              </div>
              <button
                className="text-gray-400 hover:text-white transition-colors"
                onClick={() => setSelectedLetter(null)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <h3 className="text-xs text-gray-500 font-bold uppercase tracking-wider">Subject</h3>
                <h1 className="text-xl font-bold text-white mt-1">{selectedLetter.subject}</h1>
              </div>
              <div className="grid grid-cols-2 gap-4 border-y border-[#313D4F] py-3 my-2 text-xs">
                <div>
                  <span className="text-gray-500 font-medium">Recipients:</span>
                  <span className="text-white ml-2">{selectedLetter.recipients}</span>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Log ID:</span>
                  <span className="text-white ml-2">#{selectedLetter.id}</span>
                </div>
              </div>
              <div>
                <h3 className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2">Message Body</h3>
                <div
                  className="bg-[#131212] p-5 rounded-xl border border-[#4B4B4B] text-gray-300 text-sm overflow-x-auto leading-relaxed select-text"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(selectedLetter.content) }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-[#313D4F] flex justify-end">
              <button
                className="px-6 py-2 rounded-xl bg-[#353535] hover:bg-[#484848] text-white font-semibold transition-colors"
                onClick={() => setSelectedLetter(null)}
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </DefaultLayout>
  );
};

export default NewLetter;
